import type { Static } from "@sinclair/typebox";
import {
  ASSIGNABLE_POLICY_LEVELS,
  type AssignablePolicyLevel,
  type TagPolicyEntry,
} from "@volgo/core/contract";
import { type FormEvent, useEffect, useId, useState } from "react";
import { useSession } from "../../session";
import { Button } from "../../ui/Button";
import { describeError } from "../../ui/errors";
import { formatCount, formatDateTime } from "../../ui/format";
import { ErrorNote, Loading } from "../../ui/States";
import { useLoad } from "../../ui/useLoad";

type Entry = Static<typeof TagPolicyEntry>;

const LEVEL_LABELS: Record<AssignablePolicyLevel, string> = { warn: "경고", block: "차단" };

/**
 * Tag rules. A tag is set to "warn" (blurred cover, confirm before downloading or reading) or "block"
 * (no cover, no download, no reader). Rules the server marks as fixed are shown without any control.
 */
export function TagPoliciesPage() {
  const { client } = useSession();
  const list = useLoad(() => client.call("policy.list"), "tag-policies");
  const [tag, setTag] = useState("");
  const [level, setLevel] = useState<AssignablePolicyLevel | "hard">("warn");
  const [namespace, setNamespace] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const listId = useId();

  const needle = tag.trim();
  useEffect(() => {
    if (!needle) {
      setSuggestions([]);
      return;
    }
    let alive = true;
    const timer = setTimeout(() => {
      client.call("catalog.suggestTags", { query: { q: needle.slice(0, 100), limit: 10 } }).then(
        (res) => alive && setSuggestions(res.items.map((i) => i.tag)),
        () => alive && setSuggestions([]),
      );
    }, 150);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [client, needle]);

  function run(action: Promise<unknown>) {
    setBusy(true);
    setError(null);
    action
      .then(
        () => list.reload(),
        (e: unknown) => setError(e),
      )
      .finally(() => setBusy(false));
  }
  function add(e: FormEvent) {
    e.preventDefault();
    if (!needle) return;
    const action =
      level === "hard"
        ? client.call("policy.addHard", {
            body: { tag: namespace.trim() ? `${namespace.trim()}:${needle}` : needle },
          })
        : client.call("policy.upsert", { body: { tag: needle, level } });
    run(action.then(() => setTag("")));
  }
  const change = (entry: Entry, next: AssignablePolicyLevel) =>
    run(client.call("policy.upsert", { body: { tag: entry.tag, level: next } }));
  const remove = (entry: Entry) => run(client.call("policy.remove", { query: { tag: entry.tag } }));

  return (
    <section aria-label="태그 정책">
      <p className="page-lede">
        경고로 정한 태그의 갤러리는 표지가 흐리게 나오고, 확인한 뒤에 받거나 읽을 수 있습니다. 차단으로 정한
        태그의 갤러리는 표지가 나오지 않고 받거나 읽을 수 없습니다. 고정 차단은 바꾸거나 지울 수 없습니다.
      </p>
      <form className="inline-form" onSubmit={add}>
        <label className="field">
          태그
          <input
            className="input"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            list={level === "hard" ? undefined : listId}
            placeholder={level === "hard" ? "태그 이름" : "이름공간:이름"}
            pattern={level === "hard" ? "[^:]+" : undefined}
            maxLength={200}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
          />
        </label>
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        <label className="field">
          수준
          <select
            className="select"
            value={level}
            onChange={(e) => setLevel(e.target.value as AssignablePolicyLevel | "hard")}
          >
            {ASSIGNABLE_POLICY_LEVELS.map((l) => (
              <option key={l} value={l}>
                {LEVEL_LABELS[l]}
              </option>
            ))}
            <option value="hard">하드 차단 (추가만 가능)</option>
          </select>
        </label>
        {level === "hard" && (
          <label className="field">
            접두사 (선택)
            <input
              className="input"
              value={namespace}
              maxLength={198}
              onChange={(e) => setNamespace(e.target.value)}
            />
          </label>
        )}
        {level === "hard" && (
          <p>
            등록 규칙: {namespace.trim() ? `${namespace.trim()}:${needle}` : needle} ·{" "}
            {namespace.trim() ? "해당 이름공간에만 적용" : "모든 이름공간에 적용"}
          </p>
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={
            busy || !needle || (level === "hard" && (needle.includes(":") || namespace.includes(":")))
          }
        >
          규칙 저장
        </Button>
      </form>
      {error !== null && (
        <p className="note note-warn" role="alert">
          {describeError(error, { NOT_FOUND: "이미 지워진 규칙입니다. 목록을 새로 고칩니다." })}
        </p>
      )}

      {list.status === "loading" && <Loading />}
      {list.status === "error" && <ErrorNote error={list.error} onRetry={list.reload} />}
      {list.status === "ready" && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">태그</th>
                <th scope="col">수준</th>
                <th scope="col">갤러리 수</th>
                <th scope="col">바꾼 때</th>
                <th scope="col">
                  <span className="sr">동작</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((entry) => (
                <tr key={`${entry.fixed}:${entry.tag}`} data-fixed={entry.fixed ? "true" : undefined}>
                  <th scope="row">{entry.tag}</th>
                  <td>
                    {entry.fixed ? (
                      "차단 (고정)"
                    ) : (
                      <select
                        className="select"
                        aria-label={`${entry.tag} 수준`}
                        value={entry.level}
                        disabled={busy}
                        onChange={(e) => change(entry, e.target.value as AssignablePolicyLevel)}
                      >
                        {ASSIGNABLE_POLICY_LEVELS.map((l) => (
                          <option key={l} value={l}>
                            {LEVEL_LABELS[l]}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>{formatCount(entry.galleryCount)}</td>
                  <td>
                    {entry.updatedAt ? `${formatDateTime(entry.updatedAt)} ${entry.updatedBy ?? ""}` : "—"}
                  </td>
                  <td>
                    {!entry.fixed && (
                      <Button
                        disabled={busy}
                        aria-label={`${entry.tag} 규칙 지우기`}
                        onClick={() => remove(entry)}
                      >
                        규칙 지우기
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
